import type { ChangeEvent } from 'react';
import { useCallback, useState } from 'react';
import {
  IntentMLService,
  IntentMLJobStatus,
  IntentMLPreviewResult,
  IntentMLValidationResult,
} from '../../services/api/intentmlService';

export type MergeStrategy = 'SKIP_EXISTING' | 'MERGE' | 'UPDATE_EXISTING' | 'CREATE_NEW';

export function useIntentMLPrimeImport(onImportComplete: () => void) {
  const [doc, setDoc] = useState<Record<string, unknown> | null>(null);
  const [mergeStrategy, setMergeStrategy] = useState<MergeStrategy>('MERGE');
  const [validation, setValidation] = useState<IntentMLValidationResult | null>(null);
  const [preview, setPreview] = useState<IntentMLPreviewResult | null>(null);
  const [job, setJob] = useState<IntentMLJobStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const f = event.target.files?.[0];
    if (!f) return;
    if (f.type !== 'application/json') {
      setError('Please select a JSON file');
      return;
    }
    setError(null);
    setValidation(null);
    setPreview(null);
    setJob(null);
    try {
      const text = await f.text();
      setDoc(JSON.parse(text) as Record<string, unknown>);
    } catch {
      setError('Invalid JSON');
      setDoc(null);
    }
  };

  const runValidate = useCallback(async () => {
    if (!doc) return;
    setLoading(true);
    setError(null);
    try {
      const v = await IntentMLService.validatePrime(doc, 'full');
      setValidation(v);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validate failed');
    } finally {
      setLoading(false);
    }
  }, [doc]);

  const runPreview = useCallback(async () => {
    if (!doc) return;
    setLoading(true);
    setError(null);
    try {
      const p = await IntentMLService.previewPrime(doc, {}, 'full');
      setPreview(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Preview failed');
    } finally {
      setLoading(false);
    }
  }, [doc]);

  const runApply = useCallback(async () => {
    if (!doc) return;
    setLoading(true);
    setError(null);
    setJob(null);
    try {
      const idempotencyKey = crypto.randomUUID();
      const previewHash = preview?.preview_hash;
      const apply = await IntentMLService.applyPrime({
        document: doc,
        idempotency_key: idempotencyKey,
        preview_hash: previewHash ?? null,
        merge_strategy: mergeStrategy,
      });
      const final = await IntentMLService.pollJobUntilTerminal(apply.job_id, {
        intervalMs: 400,
        maxMs: 120_000,
      });
      setJob(final);
      if (final.status === 'completed') {
        onImportComplete();
      } else if (final.status === 'failed') {
        setError(final.error_message || 'Apply job failed');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Apply failed');
    } finally {
      setLoading(false);
    }
  }, [doc, preview, mergeStrategy, onImportComplete]);

  return {
    doc,
    mergeStrategy,
    setMergeStrategy,
    validation,
    preview,
    job,
    loading,
    error,
    loadFile,
    runValidate,
    runPreview,
    runApply,
  };
}
